import { VOOMA_USER_EMAIL } from '@shared/constants';
import { useEmailThreadContext } from '@shared/contexts/pluginContexts/EmailThreadContext';
import {
  QuoteEventType,
  useGetFeedQuoteEventsQuery,
} from '@shared/generated/graphql';
import { roundToNearestMinutes } from 'date-fns';
import { groupBy } from 'lodash';
import { useMemo } from 'react';
import { FeedItemType, FeedQuoteEvent, QuoteEventsFeedItem } from '../types';

const groupQuoteEvents = (events: FeedQuoteEvent[]) =>
  groupBy(events, (item) => {
    const dateTime = new Date(item.createdAt);
    const roundedTime = roundToNearestMinutes(dateTime).toISOString();
    return `${item.type}-${roundedTime}`;
  });

type PartialQuoteEvent = {
  __typename?: 'QuoteEvent';
  id: string;
  quoteId: string;
  createdAt: any;
  type: QuoteEventType;
  metadata?: any | null;
  user?: {
    __typename?: 'User';
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    id: string;
  } | null;
};

const shouldIncludeEvent = (event: PartialQuoteEvent) => {
  if (
    event.user?.email === VOOMA_USER_EMAIL &&
    event.type.startsWith('MARKED_AS')
  )
    return true;

  return [
    QuoteEventType.AUTO_REPLY_EVALUATOR_RESPONSE,
    QuoteEventType.AUTO_REPLY_DRAFTED,
    QuoteEventType.AUTO_REPLY_SENT,
    QuoteEventType.AUTO_REPLY_DRAFT_FAILURE,
    QuoteEventType.AUTO_REPLY_SEND_FAILURE,
    QuoteEventType.USER_CONFIRMED,
  ].includes(event.type);
};

export const useFeedQuoteEvents = () => {
  const { threadJobs } = useEmailThreadContext();
  const jobIds = threadJobs.jobs.map((job) => job.id);

  // TODO(parlato): Another place where we're polling too often
  // and should instead be subscribing to changes on the backend
  const { data } = useGetFeedQuoteEventsQuery({
    variables: { input: { ids: jobIds } },
    pollInterval: 10 * 1000,
  });

  const events: QuoteEventsFeedItem[] = useMemo(() => {
    const quotes = data?.jobs.edges.flatMap((job) => job?.node?.quotes);

    const quoteEvents = quotes
      ?.flatMap((quote) => quote?.quoteEvents)
      .filter(shouldIncludeEvent);

    const feedQuoteEvents = quoteEvents?.map((qe) => {
      return {
        createdAt: qe.createdAt,
        type: qe.type,
        quoteId: qe.quoteId,
        metadata: qe.metadata,
      } as FeedQuoteEvent;
    });

    if (!feedQuoteEvents) {
      return [];
    }

    const groupedEvents = groupQuoteEvents(feedQuoteEvents);

    return Object.values(groupedEvents).map((events) => {
      return {
        type: FeedItemType.QUOTE_EVENTS,
        createdAt: events[0].createdAt,
        createdByDisplay: 'Vooma',
        data: {
          quoteEvents: events,
        },
      };
    });
  }, [data]);

  return {
    events,
  };
};
